import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";
import { getSession, saveCredentials, sessions } from "./session";
import type { BuildSelection, CardDef, Command, GameView, Scenario, Session } from "./types";
import { scenarioDescriptions, scenarioLabels, statusLabels } from "./gameMeta";
import { useGame } from "./useGame";
import { PrincipalityBoard } from "./components/Board";
import { ChoiceModal } from "./components/ChoiceModal";
import { ActionPanel } from "./components/ActionPanel";
import { Supply, rollPresentationKey } from "./components/Supply";
import { RecoveryPanel } from "./components/RecoveryPanel";
import { BoardInteractionProvider } from "./components/BoardInteraction";
import { ResourceAmounts } from "./components/ResourceIcon";

export default function App() {
  const [route, setRoute] = useState(() => parseRoute());
  const [definitions, setDefinitions] = useState<CardDef[]>([]);
  useEffect(() => {
    const updateRoute = () => setRoute(parseRoute());
    window.addEventListener("popstate", updateRoute);
    return () => window.removeEventListener("popstate", updateRoute);
  }, []);
  useEffect(() => { api.cards().then(setDefinitions).catch(() => undefined); }, []);
  if (route.kind === "game") {
    const session = getSession(route.gameId);
    return session ? <GamePage session={session} definitions={definitions} /> : <JoinPage gameId={route.gameId} initialCode={route.code} />;
  }
  if (route.kind === "join") return <JoinPage gameId={route.gameId} initialCode={route.code} />;
  return <HomePage />;
}

function HomePage() {
  const [name, setName] = useState("");
  const [scenario, setScenario] = useState<Scenario>("introductie");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const saved = sessions();
  const create = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError(undefined);
    try {
      const session = saveCredentials(await api.create(name, scenario));
      navigate(`/spel/${session.gameId}`);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Spel maken mislukt"); setBusy(false); }
  };
  return (
    <main className="landing">
      <nav className="landing-nav"><Brand /><span>Privé · 2 spelers · Nederlands</span></nav>
      <section className="hero">
        <div className="hero-copy"><span className="eyebrow">Samen, waar jullie ook zijn</span><h1>Bouw een vorstendom.<br /><em>Win het duel.</em></h1><p>Een complete digitale speeltafel voor twee. Beurten worden direct gesynchroniseerd en iedere spelstand blijft veilig bewaard.</p><div className="feature-row"><span>✦ Realtime</span><span>Ⅱ Pauzeren</span><span>↶ Samen herstellen</span></div></div>
        <form className="create-card" onSubmit={create}><span className="eyebrow">Nieuw duel</span><h2>Nodig je tegenstander uit</h2><label>Jouw naam<input required maxLength={32} autoComplete="nickname" value={name} onChange={(event) => setName(event.target.value)} placeholder="Bijvoorbeeld David" /></label><fieldset><legend>Spelvariant</legend><div className="scenario-list">{(Object.keys(scenarioLabels) as Scenario[]).map((value) => <label className={scenario === value ? "selected" : ""} key={value}><input type="radio" name="scenario" value={value} checked={scenario === value} onChange={() => setScenario(value)} /><span><strong>{scenarioLabels[value]}</strong><small>{scenarioDescriptions[value]}</small></span><i>✓</i></label>)}</div></fieldset>{error ? <div className="inline-error">{error}</div> : null}<button className="primary create-button" disabled={busy}>{busy ? "Duel voorbereiden…" : "Duel maken"}<span>→</span></button></form>
      </section>
      {saved.length ? <section className="saved-games"><div><span className="eyebrow">Op dit apparaat</span><h2>Verder met een duel</h2></div><div className="saved-list">{saved.map((session) => <button key={session.gameId} onClick={() => navigate(`/spel/${session.gameId}`)}><span className={`session-shield seat-${session.seat}`}>♛</span><span><strong>Spel {session.gameId.slice(0, 8)}</strong><small>{session.seat === "rood" ? "Rode" : "Blauwe"} speler · bewaard {new Date(session.savedAt).toLocaleDateString("nl-NL")}</small></span><b>→</b></button>)}</div></section> : null}
      <footer className="landing-footer">Vorstenduel is een particuliere, niet-commerciële webimplementatie met oorspronkelijke vormgeving.</footer>
    </main>
  );
}

function JoinPage({ gameId, initialCode }: { gameId: string; initialCode?: string }) {
  const [name, setName] = useState(""); const [code, setCode] = useState(initialCode || ""); const [busy, setBusy] = useState(false); const [error, setError] = useState<string>();
  const join = async (event: React.FormEvent) => { event.preventDefault(); setBusy(true); setError(undefined); try { const session = saveCredentials(await api.join(gameId, name, code)); navigate(`/spel/${session.gameId}`); } catch (cause) { setError(cause instanceof Error ? cause.message : "Aansluiten mislukt"); setBusy(false); } };
  return <main className="join-page"><button className="back-link" onClick={() => navigate("/")}>← Terug</button><section className="join-crest"><span>♛</span><h1>Je bent uitgenodigd</h1><p>Vul je naam en de gedeelde code in. Alleen jullie twee krijgen toegang tot deze tafel.</p></section><form className="join-card" onSubmit={join}><label>Jouw naam<input required maxLength={32} value={name} onChange={(event) => setName(event.target.value)} autoFocus /></label><label>Uitnodigingscode<input required value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} placeholder="ABCD-EFGH" /></label>{error ? <div className="inline-error">{error}</div> : null}<button className="primary" disabled={busy}>{busy ? "Aansluiten…" : "Aan tafel gaan"}</button></form></main>;
}

export function GamePage({ session, definitions }: { session: Session; definitions: CardDef[] }) {
  const { game, connection, error, setError, busy, pendingOffline, command, refresh } = useGame(session);
  const defs = useMemo(() => new Map(definitions.map((definition) => [definition.id, definition])), [definitions]);
  const [regions, setRegions] = useState<number[]>([]); const [recovery, setRecovery] = useState(false); const [pause, setPause] = useState(false); const [rules, setRules] = useState(false);
  const [buildSelection, setBuildSelection] = useState<BuildSelection | null>(null);
  const [boardCommand, setBoardCommand] = useState<{ id: number; command: Command } | null>(null);
  const rollAcknowledgementStorageKey = `catan-duel:roll-ack:${session.gameId}:${session.seat}`;
  const [acknowledgedRoll, setAcknowledgedRoll] = useState<string | null>(() => localStorage.getItem(rollAcknowledgementStorageKey));
  const commandSequence = useRef(0);
  const boardViewport = useRef<HTMLDivElement>(null);
  const boardSurface = useRef<HTMLDivElement>(null);
  const automaticZoom = useRef(localStorage.getItem("catan-duel:bordzoom") === null);
  const [boardZoom, setBoardZoomState] = useState(() => {
    const saved = Number(localStorage.getItem("catan-duel:bordzoom"));
    return Number.isFinite(saved) && saved >= 0.4 && saved <= 1.25 ? saved : 0.58;
  });
  const setBoardZoom = useCallback((value: number) => {
    const next = Math.min(1.25, Math.max(0.4, Math.round(value * 100) / 100));
    setBoardZoomState(next);
    localStorage.setItem("catan-duel:bordzoom", String(next));
  }, []);
  const fitBoard = useCallback(() => {
    const viewport = boardViewport.current;
    const surface = boardSurface.current;
    if (!viewport || !surface) return;
    const domainWidths = Array.from(surface.querySelectorAll<HTMLElement>(".domain-stage"), (domain) => domain.offsetWidth);
    const naturalWidth = Math.max(900, ...domainWidths);
    const naturalHeight = surface.scrollHeight;
    if (!naturalWidth || !naturalHeight || !viewport.clientWidth || !viewport.clientHeight) return;
    setBoardZoom(Math.min((viewport.clientWidth - 20) / naturalWidth, (viewport.clientHeight - 20) / naturalHeight, 1));
  }, [setBoardZoom]);
  const boardShape = game ? game.players.map((player) => [
    player.principality.towns.map((town) => town.kind).join(","),
    player.principality.upper_regions.some((region) => region.expansion),
    player.principality.lower_regions.some((region) => region.expansion),
  ].join(":" )).join("|") : "";
  const visibleBoardShape = `${boardShape}|${buildSelection?.instance.card_id || ""}`;
  useEffect(() => {
    if (!game || !automaticZoom.current) return;
    const timer = window.setTimeout(fitBoard, 60);
    return () => window.clearTimeout(timer);
  }, [fitBoard, game, visibleBoardShape]);
  useEffect(() => {
    const resize = () => { if (automaticZoom.current) fitBoard(); };
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [fitBoard]);
  useEffect(() => {
    setAcknowledgedRoll(localStorage.getItem(rollAcknowledgementStorageKey));
    const syncAcknowledgement = (event: StorageEvent) => {
      if (event.key === rollAcknowledgementStorageKey) setAcknowledgedRoll(event.newValue);
    };
    window.addEventListener("storage", syncAcknowledgement);
    return () => window.removeEventListener("storage", syncAcknowledgement);
  }, [rollAcknowledgementStorageKey]);
  const send = useCallback((value: Command) => { void command(value); }, [command]);
  const queueBoardCommand = useCallback((value: Command) => {
    commandSequence.current += 1;
    setBoardCommand({ id: commandSequence.current, command: value });
  }, []);
  const handleBoardCommand = useCallback((id: number) => {
    setBoardCommand((current) => current?.id === id ? null : current);
  }, []);
  useEffect(() => {
    if (!game || !buildSelection) return;
    const own = game.players[game.viewer === "rood" ? 0 : 1];
    const exists = buildSelection.source === "open"
      ? game.supply.open_cards.some((card) => card.instance_id === buildSelection.instance.instance_id)
      : own.hand?.some((card) => card.instance_id === buildSelection.instance.instance_id);
    const legal = game.build_options?.cards.find((option) => (
      option.instance_id === buildSelection.instance.instance_id
      && option.from_open === (buildSelection.source === "open")
    ));
    if (game.status !== "actief" || game.turn.active !== game.viewer || game.turn.phase !== "actiefase" || !exists || (game.build_options && !legal)) {
      setBuildSelection(null);
    } else if (legal && (buildSelection.discount_resource
      ? !legal.discount_resources.includes(buildSelection.discount_resource)
      : !legal.can_build_without_discount)) {
      setBuildSelection({ ...buildSelection, discount_resource: legal.discount_resources[0] });
    }
  }, [buildSelection, game]);
  const selectRegion = (id: number) => setRegions((current) => current.includes(id) ? current.filter((value) => value !== id) : current.length < 2 ? [...current, id] : [current[1], id]);
  const currentRollPresentation = game ? rollPresentationKey(game) : null;
  const awaitingRollAcknowledgement = Boolean(currentRollPresentation && acknowledgedRoll !== currentRollPresentation);
  const acknowledgeRoll = () => {
    if (!currentRollPresentation) return;
    localStorage.setItem(rollAcknowledgementStorageKey, currentRollPresentation);
    setAcknowledgedRoll(currentRollPresentation);
  };
  if (!game) return <main className="loading-screen"><Brand /><span className="spinner large" /><p>{error || "Beveiligde spelstand laden…"}</p><button onClick={() => refresh()}>Opnieuw proberen</button></main>;
  const ownIndex = game.viewer === "rood" ? 0 : 1; const opponentIndex = ownIndex ? 0 : 1; const own = game.players[ownIndex];
  const invite = `${location.origin}/meedoen/${session.gameId}?code=${encodeURIComponent(session.joinCode || "")}`;
  const arrangingSetup = game.status === "voorbereiding" && game.turn.phase === "landschappen_ordenen";
  const arrangingNewVillage = game.status === "actief"
    && game.turn.phase === "actiefase"
    && game.turn.active === game.viewer
    && game.turn.built_settlement_region_ids.length === 2;
  const selectPlayableRegion = arrangingSetup
    ? selectRegion
    : arrangingNewVillage
      ? (id: number) => {
          if (game.turn.built_settlement_region_ids.includes(id)) selectRegion(id);
        }
      : undefined;
  return (
    <BoardInteractionProvider>
    <div className="game-shell">
      <header className="game-topbar"><Brand /><div className="game-status"><span className={`connection-dot ${connection}`} /> <strong>{statusLabels[game.status]}</strong><small>revisie {game.version}{pendingOffline ? ` · ${pendingOffline} offline bewaard` : ""}</small></div><div className="top-actions"><div className="zoom-controls" role="group" aria-label="Bordzoom"><button aria-label="Uitzoomen" title="Uitzoomen" onClick={() => { automaticZoom.current = false; setBoardZoom(boardZoom - 0.08); }}>−</button><button className="zoom-fit" onClick={() => { automaticZoom.current = true; fitBoard(); }} title="Hele tafel passend maken">Passend · {Math.round(boardZoom * 100)}%</button><button aria-label="Inzoomen" title="Inzoomen" onClick={() => { automaticZoom.current = false; setBoardZoom(boardZoom + 0.08); }}>+</button></div><button onClick={() => setRules(true)}>Speluitleg</button><button onClick={() => setRecovery(true)}>↶ Tijdlijn</button><button onClick={() => game.status === "gepauzeerd" ? send({ type: "resume" }) : setPause(true)}>{game.status === "gepauzeerd" ? "▶ Hervatten" : "Ⅱ Pauzeren"}</button></div></header>
      {error ? <div className="global-notice"><span>{error}</span><button onClick={() => setError(undefined)}>×</button></div> : null}
      {game.status === "wachten_op_tegenstander" ? <section className="invite-banner"><div><span className="eyebrow">Je tafel is klaar</span><h2>Deel deze uitnodiging</h2><p>Code <strong>{session.joinCode}</strong></p></div><button className="primary" onClick={() => navigator.clipboard.writeText(invite)}>Uitnodigingslink kopiëren</button></section> : null}
      {game.winner ? <section className="winner-banner"><span>♛</span><div><small>Het duel is beslist</small><h2>{game.players[game.winner === "rood" ? 0 : 1].name} wint!</h2></div></section> : null}
      <main className="table-layout">
        <div className="board-viewport" ref={boardViewport}>
          <div className="board-zoom-surface" ref={boardSurface} style={{ zoom: boardZoom }}>
            <div className="board-column">
              <PrincipalityBoard player={game.players[opponentIndex]} score={game.scores[opponentIndex]} definitions={defs} active={game.turn.active === game.players[opponentIndex].seat} viewer={game.viewer} placementResetKey={game.recovery_note?.at} />
              <Supply game={game} definitions={defs} showRollResult={awaitingRollAcknowledgement} onContinue={acknowledgeRoll} />
              <PrincipalityBoard
                player={own}
                score={game.scores[ownIndex]}
                definitions={defs}
                active={game.turn.active === own.seat}
                viewer={game.viewer}
                selectedRegions={regions}
                onRegion={selectPlayableRegion}
                buildSelection={buildSelection}
                canBuild={game.status === "actief" && game.turn.active === game.viewer && game.turn.phase === "actiefase"}
                busy={busy}
                supply={game.supply}
                buildOptions={game.build_options}
                onBuildCommand={queueBoardCommand}
                placementResetKey={game.recovery_note?.at}
              />
            </div>
          </div>
        </div>
        <ActionPanel game={game} definitions={defs} busy={busy} selectedRegions={regions} onClearRegions={() => setRegions([])} onCommand={send} selectedBuild={buildSelection} onSelectBuild={setBuildSelection} boardCommand={boardCommand} onBoardCommandHandled={handleBoardCommand} />
      </main>
      <ChoiceModal game={game} definitions={defs} busy={busy} suspended={awaitingRollAcknowledgement} onResolve={(response, plan) => {
        const choice = { type: "resolve_choice" as const, choice_id: game.pending_choice!.choice_id, response };
        send(plan ? { type: "with_resource_plan", plan, command: choice } : choice);
      }} />
      {pause ? <PauseDialog busy={busy} onClose={() => setPause(false)} onPause={(reason) => { send({ type: "pause", reason }); setPause(false); }} /> : null}
      {recovery ? <RecoveryPanel session={session} currentVersion={game.version} onClose={() => setRecovery(false)} onChanged={() => void refresh()} /> : null}
      {rules ? <RulesDialog scenario={game.scenario} onClose={() => setRules(false)} /> : null}
    </div>
    </BoardInteractionProvider>
  );
}

function PauseDialog({ busy, onClose, onPause }: { busy: boolean; onClose: () => void; onPause: (reason?: string) => void }) { const [reason, setReason] = useState(""); return <div className="modal-layer"><section className="modal small-modal"><span className="pause-glyph">Ⅱ</span><h2>Spel pauzeren?</h2><p>De volledige stand blijft op de server staan. Geen van beiden kan spelen totdat het duel wordt hervat.</p><label>Notitie voor later (optioneel)<textarea maxLength={240} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Bijvoorbeeld: zaterdagavond verder" /></label><div className="modal-actions"><button onClick={onClose}>Annuleren</button><button className="primary" disabled={busy} onClick={() => onPause(reason || undefined)}>Pauzeren</button></div></section></div>; }

export function RulesDialog({ scenario, onClose }: { scenario: Scenario; onClose: () => void }) {
  return (
    <div className="modal-layer">
      <section className="modal rules-modal">
        <header className="modal-titlebar">
          <div>
            <span className="eyebrow">Beknopte spelhulp</span>
            <h2>{scenarioLabels[scenario]}</h2>
          </div>
          <button className="icon-button" aria-label="Speluitleg sluiten" onClick={onClose}>×</button>
        </header>
        <div className="rules-columns">
          <article className="rules-production-source">
            <span className="eyebrow">Regelcontrole · met bronnen</span>
            <h3>Waar wordt een opbrengst opgeslagen?</h3>
            <p>
              Bij een opbrengstworp draai je <strong>het landschap met het geworpen getal</strong>.
              Heb je twee goudvelden en produceert er maar één, dan komt het goud dus op dat
              producerende veld. Je kiest niet tussen beide velden.
            </p>
            <p>
              Is het producerende landschap al vol (3 grondstoffen), dan gaat de extra grondstof
              verloren. Die verhuist niet naar een ander landschap van hetzelfde type.
            </p>
            <p>
              <strong>Uitzondering — Verborgen goudmijn:</strong> zolang je deze kaart hebt, mag je
              ieder <em>nieuw verkregen</em> goud vrij op je goudvelden of in de goudmijn opslaan.
              Goud dat al ergens ligt, mag je niet later alsnog verplaatsen.
            </p>
            <div className="rules-source-links" aria-label="Bronnen voor de opbrengstregel">
              <a href="https://spellenlab.be/media/files/0f96bb1845d59fb54ee829b1626b179aa3c8b6fd.pdf#page=2" target="_blank" rel="noreferrer">
                Nederlandse spelregels · gedrukte pagina 3
              </a>
              <a href="https://www.catan.de/faq/die-karten-von-catan-das-duell" target="_blank" rel="noreferrer">
                Officiële CATAN-FAQ · vragen 46, 48 en 52–55
              </a>
            </div>
          </article>
          <article className="rules-scout-source">
            <span className="eyebrow">Kaarttiming · met bron</span>
            <h3>Hoe werkt Verkenner bij een dorp?</h3>
            <p>
              Speel <strong>Verkenner vóór de dorpbouw</strong>. Daarna moet je dat dorp bouwen.
              In plaats van de normale willekeurige uitkomst kies je twee landschappen uit de
              volledige landschapsstapel: de eerste komt boven en de tweede onder het nieuwe dorp.
              De overige kaarten worden daarna geschud.
            </p>
            <p>
              De twee kaarten die zonder Verkenner boven op de stapel lagen, blijven onderdeel van
              die volledige keuze en mogen dus ook allebei blijven liggen.
            </p>
            <div className="rules-source-links" aria-label="Bron voor de Verkenner-regel">
              <a href="https://www.catan.com/sites/default/files/2021-06/rivals_for_catan_rules_200309.pdf#page=18" target="_blank" rel="noreferrer">
                Officiële CATAN-spelregels · kaartindex Verkenner
              </a>
            </div>
          </article>
          <article>
            <h3>Je beurt</h3>
            <ol>
              <li>Werp beide dobbelstenen.</li>
              <li>Verwerk opbrengst en gebeurtenis.</li>
              <li>Bouw, ruil en speel actiekaarten.</li>
              <li>Vul je hand aan tot de limiet.</li>
              <li>Ruil eventueel één handkaart en sluit af.</li>
            </ol>
          </article>
          <article className="rules-costs">
            <h3>Bouwkosten</h3>
            <p><strong>Straat</strong><ResourceAmounts resources={["hout", "baksteen", "baksteen"]} /></p>
            <p><strong>Dorp</strong><ResourceAmounts resources={["hout", "baksteen", "graan", "wol"]} /></p>
            <p><strong>Stad</strong><ResourceAmounts resources={["graan", "graan", "erts", "erts", "erts"]} /></p>
          </article>
          <article>
            <h3>Voordelen</h3>
            <p>Minstens 3 kracht en meer dan de ander geeft het krachtvoordeel. Hetzelfde geldt voor handel. Elk voordeel telt als 1 zegepunt.</p>
            <p>Je handlimiet is 3 plus je vooruitgangspunten.</p>
          </article>
          <article>
            <h3>Doel</h3>
            <p>Bereik {scenario === "introductie" ? 7 : scenario === "duel_der_vorsten" ? 13 : 12} zegepunten. Alleen in het introductiespel wordt de overwinning aan het einde van je beurt vastgesteld; in thema- en duelspellen win je direct tijdens je eigen beurt.</p>
          </article>
        </div>
        <button className="primary" onClick={onClose}>Terug naar het spel</button>
      </section>
    </div>
  );
}

function Brand() { return <button className="brand" onClick={() => navigate("/")} aria-label="Naar start"><span>♛</span><span><strong>VORSTEN</strong><small>DUEL</small></span></button>; }
function parseRoute(): { kind: "home" } | { kind: "game" | "join"; gameId: string; code?: string } { const match = location.pathname.match(/^\/(spel|meedoen)\/([a-zA-Z0-9]+)/); if (!match) return { kind: "home" }; return { kind: match[1] === "spel" ? "game" : "join", gameId: match[2], code: new URLSearchParams(location.search).get("code") || undefined }; }
function navigate(path: string) { history.pushState({}, "", path); window.dispatchEvent(new PopStateEvent("popstate")); }
